/**
 * Google Tag Manager utility functions
 */

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

// Store the GTM ID for reference
let gtmId: string | null = null;

/**
 * Initialize Google Tag Manager
 * @param containerId - Google Tag Manager container ID (e.g., 'GTM-XXXXXXXXXX')
 */
export function initializeGoogleTagManager(containerId?: string): void {
  // Get GTM ID from parameter, environment variable, or use default
  const gtmContainerId = containerId || import.meta.env.VITE_GTM_CONTAINER_ID || 'GTM-582XKZW9';
  
  if (!gtmContainerId) {
    console.warn('Google Tag Manager container ID not provided');
    return;
  }

  // Prevent double initialization
  if (window.dataLayer && window.dataLayer.find((item: any) => item.gtmStart)) {
    return;
  }

  // Store GTM ID
  gtmId = gtmContainerId;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Load Google Tag Manager script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}`;
  document.head.appendChild(script);

  // Add noscript iframe to body (GTM requirement)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Push data to GTM dataLayer
 * @param data - Data object to push to dataLayer
 */
export function pushToDataLayer(data: Record<string, any>): void {
  if (window.dataLayer) {
    window.dataLayer.push(data);
  }
}

/**
 * Track a page view
 * @param path - The page path to track
 */
export function trackPageView(path: string): void {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_location: window.location.href,
  });
}

/**
 * Track an event
 * @param eventName - Name of the event
 * @param eventParams - Additional event parameters
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  pushToDataLayer({
    event: eventName,
    ...eventParams,
  });
}
